import React, { useState, useRef, useCallback } from 'react';
import { Box } from '@mantine/core';

const SmartThumbnail = ({
    src,
    fallbackSrc,
    alt = '',
    containerHeight = 300,
    mobileHeight = 220,
    mobileBreakpoint = 768,
    aspectTolerance = 0.35, // allow up to 35% aspect-ratio difference before switching to "contain"
    smallImageThreshold = 0.6, // if image is <60% of frame width/height, treat as a small logo/icon
    className = '',
}) => {
    const containerRef = useRef(null);
    const [fitMode, setFitMode] = useState('contain'); // safe default until we've measured
    const [isSmallImage, setIsSmallImage] = useState(false);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= mobileBreakpoint : false
    );

    const handleLoad = useCallback(
        (event) => {
            const img = event.target;
            const { naturalWidth, naturalHeight } = img;

            if (!naturalWidth || !naturalHeight) return;

            const imageAspect = naturalWidth / naturalHeight;
            const containerWidth = containerRef.current?.offsetWidth || 0;
            const activeHeight = window.innerWidth <= mobileBreakpoint ? mobileHeight : containerHeight;
            const containerAspect = containerWidth && activeHeight ? containerWidth / activeHeight : imageAspect;

            const aspectDiff = Math.abs(imageAspect - containerAspect) / containerAspect;

            // A small source image (icon/logo) should never be stretched via
            // "cover" just because its aspect ratio happens to roughly match.
            const tooSmallToCover =
                naturalWidth < containerWidth * smallImageThreshold ||
                naturalHeight < activeHeight * smallImageThreshold;

            setIsSmallImage(tooSmallToCover);
            setFitMode(aspectDiff <= aspectTolerance && !tooSmallToCover ? 'cover' : 'contain');
        },
        [containerHeight, mobileHeight, mobileBreakpoint, aspectTolerance, smallImageThreshold]
    );

    const handleError = useCallback(
        (event) => {
            if (fallbackSrc && event.target.src !== fallbackSrc) {
                setFitMode('contain');
                setIsSmallImage(true);
                event.target.src = fallbackSrc;
            }
        },
        [fallbackSrc]
    );

    // Track breakpoint changes so the active height used for the aspect
    // calculation stays correct if the user resizes/rotates after load.
    const handleResize = useCallback(() => {
        setIsMobile(window.innerWidth <= mobileBreakpoint);
    }, [mobileBreakpoint]);

    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const activeHeight = isMobile ? mobileHeight : containerHeight;

    return (
        <Box
            ref={containerRef}
            className={`smart-thumbnail ${className}`}
            sx={() => ({
                width: '100%',
                height: activeHeight,
                background: '#F6F7F8',
                border: '1px solid #E7E9EC',
                borderRadius: 8,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            })}
        >
            <img
                src={src || fallbackSrc}
                alt={alt}
                onLoad={handleLoad}
                onError={handleError}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: fitMode,
                    objectPosition: 'center',
                    padding: isSmallImage ? 16 : 0,
                    boxSizing: 'border-box',
                    display: 'block',
                }}
            />
        </Box>
    );
};

export default SmartThumbnail;
